import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import { usePagination } from 'hooks/pagination';
import {
  PaginationData,
  Tray,
  TrayInventoryEntry,
} from 'constants/interface';
import { Search, Table } from 'UI';
import {
  cleanTrayEntriesAction,
  getTrayEntriesListAction,
  isLoadingTrayEntriesSelector,
  trayEntriesEntitiesSelector,
  trayEntriesIdsSelector,
  trayEntriesRemoteTotalSelector,
} from 'state/slices/tray';
import {
  InventoryEntryTypeEnum,
  // InventoryEntryTypeMapping,
  InventoryTypeEnum,
} from 'constants/inventory';
import { useParams } from 'react-router';
import InventoryEntryTypeLabel from 'components/InventoryEntryTypeLabel';

import {
  SearchContainer,
} from './style';

interface EntriesViewProps {
  shouldReload: boolean
  onReloadFinish(): void
}

interface TrayInventoryRouteParams {
  organizationId: string
}

const EntriesView = ({
  shouldReload,
  onReloadFinish,
}: EntriesViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(trayEntriesRemoteTotalSelector);

  const entriesEntities = useSelector(trayEntriesEntitiesSelector);
  const entriesIds = useSelector(trayEntriesIdsSelector);
  const isRemoteLoading = useSelector(isLoadingTrayEntriesSelector);

  const {
    organizationId: activeOrganizationId,
  } = useParams<TrayInventoryRouteParams>();

  const entries = entriesIds.map((entryId) => {
    const entity = entriesEntities[entryId];
    if (!entity) return undefined;
    const tableEntry: TrayInventoryEntry & {
      key?: string,
    } = {
      ...entity,
      key: entity.id,
    };
    return tableEntry;
  }).filter((tableEntry): tableEntry is TrayInventoryEntry => !!tableEntry);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    dispatch(getTrayEntriesListAction({
      ...paginationData,
      inventoryType: InventoryTypeEnum.ORGANIZATION,
      organization: activeOrganizationId,
    }));
  };
  const pagination = usePagination<TrayInventoryEntry>(
    remoteFetch,
    {
      filter: {
        type: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanTrayEntriesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  // const typeFilter = pageData.filter.type ?? null;

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar transação" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={entries}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Data"
          key="entryDate"
          dataIndex="entryDate"
          render={(entryDate: string) => {
            const date = new Date(entryDate);
            const formattedDate = date.toLocaleDateString('pt-br');
            return formattedDate;
          }}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'entryDate' ? pageData.order : undefined}
        />
        <Table.Column
          title="Tipo de transação"
          key="type"
          dataIndex="type"
          // render={(type: InventoryEntryTypeEnum) => {
          //   const formattedType = InventoryEntryTypeMapping[type].name;
          //   // TODO: maybe use icons
          //   return formattedType;
          // }}
          render={(type: InventoryEntryTypeEnum, record: TrayInventoryEntry) => (
            <InventoryEntryTypeLabel entry={record} />
          )}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'type' ? pageData.order : undefined}
          // filters={Object.values(InventoryEntryTypeEnum).map((type) => ({
          //   text: InventoryEntryTypeMapping[type].name,
          //   value: type,
          // }))}
          // filterMultiple={false}
          // filteredValue={typeFilter !== null ? [typeFilter] : null}
        />
        <Table.Column
          title="Bandeja"
          key="tray"
          dataIndex="tray"
          render={(tray: Omit<
            Tray,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => `Bandeja ${tray.capacity}${tray.isHydroponic ? ' Hidropônica' : ''}`}
          // sorter={sorter}
          // sortOrder={pageData.orderBy === 'tray' ? pageData.order : undefined}
        />
        <Table.Column
          title="Quantidade"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} bandeja${quantity > 1 ? 's' : ''}`;
            return content;
          }}
        />
        <Table.Column
          title="Taxa"
          key="price"
          dataIndex="price"
          render={(price: number, record: TrayInventoryEntry) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((price / 100) / record.quantity);
            return currencyValue;
          }}
        />
        <Table.Column
          title="Valor"
          key="price"
          dataIndex="price"
          render={(price: number) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((price / 100));
            return currencyValue;
          }}
        />
        <Table.Column
          title="Saldo"
          key="balanceDiff"
          dataIndex="balanceDiff"
          render={(balanceDiff: number) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(balanceDiff);
            const content = `${formattedQuantity} bandeja${balanceDiff > 1 ? 's' : ''}`;
            return content;
          }}
        />
      </Table>
    </>
  );
};

export default EntriesView;
